<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Event, EventCreation } from "@/types";
import { useQuery } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { computed, inject, ref } from "vue";
import EventDisplay from "./EventDisplay.vue";
import { createEventKey } from "@/logic/provide";
import { useLoginStore } from "@/stores/login";

const props = defineProps<{ caseID: string; canAdd: boolean }>();
const createEvent = inject(createEventKey);

const currentUserStore = useLoginStore();
const currentUser = computed(() => currentUserStore.currentUser);

const creationMode = ref(false);
const eventTemplate = ref<Event>(newEvent());

function newEvent(): Event {
  return {
    id: "",
    case_id: props.caseID,
    type: "Courrier Simple",
    direction: "Sortant",
    member_id: currentUser.value?.id ?? "",
    notes: "",
    creation_date: "",
  };
}

const { loading, result } = useQuery(
  gql`
    query events($case_id: String!) {
      events(case_id: $case_id) {
        id
        case_id
        legacy_id
        type
        direction
        document {
          title
          html_content
        }
        document_receipt_date
        member_id
        actor_id
        notes
        creation_date
      }
    }
  `,
  {
    case_id: props.caseID,
  }
);

const events = computed(() => {
  return (
    (result.value?.events.map((e: Event) => {
      // Fixing encoding issues that instead of omitting nil values leaves them as null.
      // Graphql is graphql, and it will include null values in the response since we asked for the field.
      let event = { ...e };
      if (event.document === null) {
        delete event.document;
      }
      if (event.legacy_id === null) {
        delete event.legacy_id;
      }
      return event;
    }) as Array<Event>) ?? []
  );
});

function onCreateNewEvent() {
  eventTemplate.value = newEvent();
  creationMode.value = true;
}

function onCreateEvent(event: EventCreation) {
  if (createEvent) {
    createEvent(event);
  } else {
    console.error("No createEvent function");
  }
  creationMode.value = false;
}

function onCancelNewEvent() {
  eventTemplate.value = newEvent();
  creationMode.value = false;
}
</script>

<template>
  <div class="events">
    <div class="history">
      <h2>Historique</h2>
    </div>
    <div class="all-events">
      <div v-if="creationMode">
        <EventDisplay
          :modelValue="eventTemplate"
          openMode="Create"
          @cancel="onCancelNewEvent"
          @create="onCreateEvent"
        />
      </div>
      <div v-else-if="caseID">
        <span @click.stop="onCreateNewEvent" class="button"
          >Enregistrer un nouvel échange au dossier</span
        >
      </div>
      <div v-else-if="!caseID">
        <span
          >Vous devez enregistrer ce dossier avant de pouvoir ajouter des
          échanges</span
        >
      </div>
      <div v-if="events.length === 0 && !loading && caseID">Aucun éléments</div>
      <template v-for="event of events" :key="event.id">
        <EventDisplay :modelValue="event" openMode="InlineList" />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.events {
  display: relative;
  width: 700px;

  .history {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;
    width: 100%;

    border-bottom-width: 6px;
    border-bottom-style: solid;
    border-color: inherit;

    margin-bottom: 1em;

    h2 {
      margin-left: 0.5em;
      color: inherit;
    }
  }

  .all-events {
    display: flex;
    flex-direction: column;
    padding-top: 1em;
    padding-left: 1em;
    row-gap: 1em;

    margin-left: auto;
    margin-right: auto;
  }
}
</style>
